import { Component, EventEmitter, Input, OnInit } from "@angular/core";
import { cloneDeep } from "lodash";
import { SrsFormWidget } from "../../../abstracts/srs-form-widget.abstract";
import {
  EdtellFormArray,
  EdtellFormGroup,
} from "../../../classes/edtell-form-group.class";
import { SrsFormState } from "../../../enumerations/form-state.enum";
import { ArrayWidgetRowAddEvent } from "../../../interfaces/events/array-widget-row-events.interface";
import { SrsFormArrayWidgetConfig } from "../../../interfaces/widgets/srs-form-array-widget.interface";
import { SrsMultiElementWidgetConfig } from "../../../interfaces/widgets/srs-multi-element-widget-config";

/**
 * This component is great if all you need to do is load data and edit it in line, but if you need to dynamically change data,
 * based up external selections, this component will need to modifications to be more reactive based upon the data values.
 */
@Component({
  selector: "app-srs-form-array-widget",
  templateUrl: "./srs-form-array-widget.component.html",
  styleUrls: ["./srs-form-array-widget.component.scss"],
})
export class SrsFormArrayWidgetComponent
  extends SrsFormWidget<any>
  implements OnInit
{
  @Input()
  config: SrsFormArrayWidgetConfig;

  headerConfig: { name: string; size: number }[];
  formGroups = new Set<EdtellFormGroup>();
  widgets = new Set<SrsMultiElementWidgetConfig>();

  formArray: EdtellFormArray;

  originalValueMap = new Map<EdtellFormGroup, any>();

  onChange = new EventEmitter<any[]>();
  onRowAdd = new EventEmitter<ArrayWidgetRowAddEvent>();
  onRowRemove = new EventEmitter<ArrayWidgetRowAddEvent>();

  constructor() {
    super();
  }

  onWidgetInit(): void {
    this.formArray = this.config.formArray;

    this.initFormConfigs();
    this.formArray.markAsOriginal();

    this.formArray.statusChanges.subscribe(() => {
      for (const f of this.formGroups) {
        f.state = this.formArray._state;
      }
    });

    this.formArray.valueChanges.subscribe(() => {
      this.onChange.emit(this.formArray.value);
    });
  }

  private initFormConfigs() {
    if (this.config.data != null) {
      for (const d of this.config.data) {
        const row = this.createRow(d);

        this.formGroups.add(row.formGroup);
        this.formArray.push(row.formGroup);
      }
    }

    // Expandable Row
    if (this.autoexpand != false) {
      this.createExpandableRow();
    }

    // Header config
    if (this.config.template.multiElementConfig.elements.length == 1) {
      this.headerConfig = [];
      const hc = this.config.template.multiElementConfig.elements[0];
      for (const h of hc) {
        this.headerConfig.push({
          name:
            h.config.title != null && h.config.title != ""
              ? h.config.title
              : h.config.key.charAt(0).toUpperCase() + h.config.key.slice(1),
          size: h.config.size,
        });
      }
    }
  }

  canDeleteRow(elm: SrsMultiElementWidgetConfig<any>) {
    let canDelete: boolean =
      this.formGroups.has(elm.formGroup) &&
      this.formArray._state != SrsFormState.READ;
    if (this.config.canDeleteRow != null) {
      canDelete = canDelete && this.config.canDeleteRow(elm);
    }
    return canDelete;
  }

  removeRow(elm: SrsMultiElementWidgetConfig<any>) {
    let index = null;

    for (const i in this.formArray.controls) {
      const control = this.formArray.controls[i];
      if (control == elm.formGroup) {
        index = i;
        break;
      }
    }

    const originalValue = this.originalValueMap.get(elm.formGroup);
    if (index != null) {
      this.formArray.removeAt(index);
      this.widgets.delete(elm);
      this.formGroups.delete(elm.formGroup);
    }

    this.onRowRemove.emit({
      data: elm.formGroup.value, // NOTE: We may want to map original values if possible
      rowGroup: elm.formGroup,
    });
  }

  public createRow(data?: any) {
    const fg = cloneDeep(this.config.template.formGroupTemplate);
    if (data != null) {
      for (const k in fg.controls) {
        const c = fg.controls[k];
        if (data[k] != null) {
          c.setValue(data[k]);
        }
      }

      this.originalValueMap.set(fg, data);
    }

    const config = cloneDeep(this.config.template.multiElementConfig);
    config.formGroup = fg;

    // Auto delete settings
    if (this.config.settings?.autoDelete == true) {
      const sub = fg.valueChanges.subscribe(() => {
        if (this.canDeleteRow(config)) {
          for (const k in fg.controls) {
            const c = fg.controls[k];
            if (c.value != null && c.value != "") {
              return;
            }
          }

          this.removeRow(config);
          sub.unsubscribe();
        }
      });
    }

    this.widgets.add(config);
    fg.setParent(this.formArray);
    this.onRowAdd.emit({
      data: data,
      rowGroup: fg,
    });

    return config;
  }

  private createExpandableRow() {
    const row = this.createRow();
    const sub = row.formGroup.valueChanges.subscribe((c) => {
      for (const k in c) {
        if (c[k] != null) {
          this.formArray.push(row.formGroup);
          this.formGroups.add(row.formGroup);

          this.createExpandableRow();
          sub.unsubscribe();
          break;
        }
      }
    });
  }

  get autoexpand() {
    return this.config.settings?.autoExpand;
  }

  get autodelete() {
    return this.config.settings?.autoDelete;
  }

  get hideDelete() {
    return this.config.settings?.hideDelete;
  }

  get showHeader() {
    return this.config.settings?.header;
  }
}
